import React, { useState } from "react";
import { Book, ExternalLink, ChevronDown, ChevronRight, Search, Zap } from "lucide-react";

const SECTIONS = [
  {
    id: "overview",
    title: "Platform Overview",
    content: `OnesuiteSEO is a self-hosted, zero-subscription SEO automation platform for managing multiple websites. It combines keyword tracking, content generation, site auditing, competitor analysis, lead capture, Google Business Profile management, and a full-featured WordPress plugin.

**Stack:** Django 5 + React 18 + WordPress Plugin
**Version:** 12.0.0 (Phase 12)`,
  },
  {
    id: "getting-started",
    title: "Getting Started",
    content: `1. **Add a website** — Settings → Add Website → enter domain and business details
2. **Install WP Plugin** — Upload onesuiteseo.zip in WordPress Admin → Plugins
3. **Connect GSC** — Site Settings → Connect Google Search Console
4. **Run an audit** — Site Audit → Run Audit`,
  },
  {
    id: "navigation",
    title: "Navigation (34 Pages)",
    items: [
      ["Dashboard", "/", "At-a-glance metrics: keywords, position, issues, vitals, leads"],
      ["Analytics", "/analytics", "Page views, clicks, scroll depth, time on page"],
      ["Keywords", "/keywords", "GSC keyword tracking, trends, position alerts"],
      ["Content", "/content", "AI content generation, queue, templates, WordPress publishing"],
      ["Competitors", "/competitors", "Keyword gap analysis, content comparison, rankings"],
      ["Landing Pages", "/pages", "AI page generation, scheduling, rank tracking"],
      ["Topic Clusters", "/topic-clusters", "Pillar/cluster content organization"],
      ["Internal Links", "/internal-links", "Link map, suggestions, auto-inject, indexing"],
      ["Calendar", "/calendar", "Visual content scheduling calendar"],
      ["Backlinks", "/backlinks", "Backlink tracking, verification, citation management"],
      ["Syndication", "/syndication", "Cross-platform content distribution"],
      ["Google Business", "/google-business", "GBP posts, reviews, Q&A, auto-generate"],
      ["Leads", "/leads", "Lead CRM, form capture, call tracking, pipeline"],
      ["Campaigns", "/campaigns", "Marketing campaign management"],
      ["Local Rankings", "/local-rankings", "Location-based rank tracking via SERP API"],
      ["Rank Tracker", "/rank-tracker", "Scheduled rank tracking jobs"],
      ["Bulk Import", "/bulk-import", "CSV import for keywords, backlinks, pages"],
      ["SEO Optimizer", "/seo-optimizer", "On-page SEO analysis, AI suggestions, WP push"],
      ["Schema Markup", "/schema", "JSON-LD schema generation (20+ types)"],
      ["Alt Text AI", "/alt-text", "AI-generated image alt text"],
      ["Image Library", "/image-library", "WordPress media library management"],
      ["Site Audit", "/audit", "Automated crawl with issue detection"],
      ["Web Vitals", "/vitals", "PageSpeed monitoring (LCP, INP, CLS)"],
      ["AI Agent", "/ai-agent", "Conversational AI SEO assistant"],
      ["AI Insights", "/ai-insights", "Content decay, keyword opportunities, autopilot"],
      ["GEO Monitor", "/geo", "AI search visibility monitoring"],
      ["Reports", "/reports", "Automated PDF reports with email delivery"],
      ["Activity Log", "/activity-log", "Full platform audit trail"],
      ["Page Designer", "/page-designer", "Visual drag-and-drop page builder"],
      ["WordPress Sync", "/wordpress-sync", "Content/settings sync with WordPress"],
      ["SEO Audit", "/seo-audit", "Deep technical SEO analysis"],
      ["SEO Maintenance", "/seo-maintenance", "Automated ongoing SEO maintenance"],
      ["Site Settings", "/site-settings", "Per-site business and connection settings"],
      ["Settings", "/settings", "Global platform and user settings"],
    ],
  },
  {
    id: "automations",
    title: "Automated Tasks (29)",
    tasks: [
      ["Daily 5 AM", "Core Web Vitals check"],
      ["Daily 6 AM", "Pull keyword data from Google Search Console"],
      ["Daily 6:30 AM", "Sync GSC data via OAuth"],
      ["Daily 7 AM", "Email alerts for keyword position drops"],
      ["Daily 8 AM", "Verify all tracked backlinks"],
      ["Daily 8:30 AM", "Alert on lost backlinks"],
      ["Daily 10 AM", "Check Google indexing status"],
      ["Daily 11 AM", "Track new page rankings"],
      ["Daily 2 AM", "Aggregate visitor analytics"],
      ["Daily 2:30 AM", "Daily SEO maintenance"],
      ["Daily 4 AM", "Process scheduled rank tracking"],
      ["Hourly", "Publish scheduled pages"],
      ["Hourly :15", "Process syndication queue"],
      ["Monday 5 AM", "Local rank scanning"],
      ["Monday 9 AM", "AI Autopilot actions"],
      ["Mon/Thu 10 AM", "Auto-syndicate new content"],
      ["Monday 12 PM", "Generate and email weekly reports"],
      ["Tuesday 4 AM", "Detect content decay"],
      ["Wednesday 3 AM", "Auto-generate content from AI insights"],
      ["Wednesday 4 AM", "Check competitor rankings"],
      ["Thursday 3 AM", "Create missing location pages"],
      ["Sunday 1 AM", "Weekly SEO maintenance"],
      ["Sunday 2 AM", "Full site audit crawl"],
      ["Sunday 3 AM", "Full Web Vitals check + backlink scan"],
      ["1st of month", "Keyword discovery + analytics cleanup"],
      ["15th of month", "GEO monitoring audit"],
    ],
  },
  {
    id: "plugin",
    title: "WordPress Plugin",
    content: `**OnesuiteSEO Plugin v2.8.2** — Replaces Rank Math / Yoast

**SEO Modules:** Meta Tags, Schema (20+ types), XML Sitemaps, Redirects (301/302/410), 404 Monitor, Auto Redirect, Google Indexing API, GA4 Analytics, llms.txt, Breadcrumbs, Robots.txt, SEO Score, Lazy Images, CSS Injection, Email Obfuscator, Page Cloner

**Content Modules:** Content Push, Link Inserter, Bulk Link Injector, Blog, Page Enhancements

**Tracking:** Page views, scroll depth, time on page, click tracking, call tracking, conversion tracking

**Lead Capture:** Contact Form 7, WPForms, Gravity Forms, Elementor Forms, Fluent Forms

**Migration:** Import from Rank Math and Yoast SEO

**Auth:** X-OnesuiteSEO-Key header | **Meta prefix:** _onesuiteseo_*`,
  },
  {
    id: "ai",
    title: "AI Features",
    content: `All AI features use **Anthropic Claude** and automatically include business context from your Website settings.

- **Content Generation** — Create SEO-optimized articles, landing pages, blog posts
- **SEO Suggestions** — AI-powered title, meta description, and H1 recommendations
- **Alt Text Generation** — AI alt text for images (optional Vision mode)
- **Schema Generation** — Auto-generate JSON-LD schema markup
- **AI Insights Autopilot** — Automatic content decay detection, keyword discovery, link suggestions
- **AI Agent** — Chat interface for SEO questions and actions
- **GBP Post Generation** — Auto-generate Google Business posts rotating through services/cities
- **Content Enrichment** — Automatic internal link and media suggestions in AI prompts`,
  },
];

export default function Documentation() {
  const [expanded, setExpanded] = useState({ overview: true, "getting-started": true });
  const [search, setSearch] = useState("");

  const toggle = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  const expandAll = () => {
    const allExpanded = SECTIONS.every((s) => expanded[s.id]);
    const next = {};
    SECTIONS.forEach((s) => (next[s.id] = !allExpanded));
    setExpanded(next);
  };

  const filteredSections = search
    ? SECTIONS.filter((s) => {
        const q = search.toLowerCase();
        if (s.title.toLowerCase().includes(q)) return true;
        if (s.content && s.content.toLowerCase().includes(q)) return true;
        if (s.items && s.items.some((i) => i.some((v) => v.toLowerCase().includes(q)))) return true;
        if (s.tasks && s.tasks.some((t) => t.some((v) => v.toLowerCase().includes(q)))) return true;
        return false;
      })
    : SECTIONS;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Book className="text-copper" size={28} />
            Documentation
          </h1>
          <p className="text-sm text-gray-500 mt-1">OnesuiteSEO v12.0.0 — Complete Platform Reference</p>
        </div>
        <div className="flex gap-3">
          <a
            href="/docs/documentation.html"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2 bg-dark-300 border border-dark-50 rounded-lg text-sm text-gray-400 hover:text-white hover:border-copper/50 transition-colors"
          >
            <ExternalLink size={14} />
            Full HTML Docs
          </a>
          <a
            href="/docs/quickstart.html"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2 bg-copper/10 border border-copper/30 rounded-lg text-sm text-copper hover:bg-copper/20 transition-colors"
          >
            <Zap size={14} />
            Quick Start
          </a>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600" />
          <input
            type="text"
            placeholder="Search documentation..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-dark-300 border border-dark-50 rounded-lg pl-10 pr-4 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-copper/50"
          />
        </div>
        <button
          onClick={expandAll}
          className="px-4 py-2.5 bg-dark-300 border border-dark-50 rounded-lg text-sm text-gray-400 hover:text-white transition-colors whitespace-nowrap"
        >
          Expand / Collapse All
        </button>
      </div>

      {filteredSections.map((section) => (
        <div key={section.id} className="bg-dark-300 border border-dark-50 rounded-xl overflow-hidden">
          <button
            onClick={() => toggle(section.id)}
            className="w-full flex items-center justify-between px-6 py-4 hover:bg-dark-50/30 transition-colors text-left"
          >
            <h2 className="text-lg font-semibold text-white">{section.title}</h2>
            {expanded[section.id] ? (
              <ChevronDown size={18} className="text-copper" />
            ) : (
              <ChevronRight size={18} className="text-gray-600" />
            )}
          </button>

          {expanded[section.id] && (
            <div className="px-6 pb-6 border-t border-dark-50 pt-4">
              {section.content && (
                <div className="text-sm text-gray-300 whitespace-pre-line leading-relaxed">
                  {section.content.split("**").map((part, i) =>
                    i % 2 === 0 ? part : <strong key={i} className="text-white">{part}</strong>
                  )}
                </div>
              )}

              {section.items && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-dark-50">
                        <th className="text-left py-2 pr-4 text-copper font-semibold">Page</th>
                        <th className="text-left py-2 pr-4 text-copper font-semibold">Route</th>
                        <th className="text-left py-2 text-copper font-semibold">Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {section.items
                        .filter(
                          (item) =>
                            !search ||
                            item.some((v) => v.toLowerCase().includes(search.toLowerCase()))
                        )
                        .map(([name, route, desc], i) => (
                          <tr key={i} className="border-b border-dark-50/50 hover:bg-dark-50/20">
                            <td className="py-2 pr-4 text-white font-medium">{name}</td>
                            <td className="py-2 pr-4">
                              <code className="text-copper text-xs bg-dark/50 px-2 py-0.5 rounded">{route}</code>
                            </td>
                            <td className="py-2 text-gray-400">{desc}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}

              {section.tasks && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-dark-50">
                        <th className="text-left py-2 pr-4 text-copper font-semibold">Schedule</th>
                        <th className="text-left py-2 text-copper font-semibold">Task</th>
                      </tr>
                    </thead>
                    <tbody>
                      {section.tasks
                        .filter(
                          (task) =>
                            !search ||
                            task.some((v) => v.toLowerCase().includes(search.toLowerCase()))
                        )
                        .map(([schedule, task], i) => (
                          <tr key={i} className="border-b border-dark-50/50 hover:bg-dark-50/20">
                            <td className="py-2 pr-4">
                              <span
                                className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${
                                  schedule.includes("Hourly")
                                    ? "bg-red-500/10 text-red-400"
                                    : schedule.includes("Daily") || schedule.includes("AM") || schedule.includes("PM")
                                    ? schedule.includes("day") || schedule.includes("Sun")
                                      ? "bg-green-500/10 text-green-400"
                                      : "bg-blue-500/10 text-blue-400"
                                    : "bg-amber-500/10 text-amber-400"
                                }`}
                              >
                                {schedule}
                              </span>
                            </td>
                            <td className="py-2 text-gray-300">{task}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
